import { useCallback, useEffect, useState } from "react";
import { isTaskSort, type TaskSort } from "@/lib/task-sort";

const STORAGE_PREFIX = "automation.boardSort.";
const SORT_EVENT = "board:sort";

const read = (boardId: string): TaskSort => {
  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + boardId);
    return isTaskSort(stored) ? stored : "manual";
  } catch {
    return "manual";
  }
};

/**
 * How one board's cards are ordered, per board and per device. A choice is broadcast
 * as a `board:sort` event, so every window showing this board follows it while a
 * window on another board keeps its own.
 */
export function useBoardSort(boardId: string): [TaskSort, (next: TaskSort) => void] {
  const [state, setState] = useState(() => ({ boardId, sort: read(boardId) }));
  // A window switched to another board reads that board's choice.
  const sort = state.boardId === boardId ? state.sort : read(boardId);
  useEffect(() => {
    if (state.boardId !== boardId) setState({ boardId, sort: read(boardId) });
  }, [boardId, state.boardId]);
  useEffect(() => {
    const onSort = (event: Event) => {
      const detail = (event as CustomEvent<{ boardId: string; sort: TaskSort }>).detail;
      if (detail.boardId === boardId) setState({ boardId, sort: detail.sort });
    };
    window.addEventListener(SORT_EVENT, onSort);
    return () => window.removeEventListener(SORT_EVENT, onSort);
  }, [boardId]);
  const choose = useCallback(
    (next: TaskSort) => {
      try {
        if (next === "manual") localStorage.removeItem(STORAGE_PREFIX + boardId);
        else localStorage.setItem(STORAGE_PREFIX + boardId, next);
      } catch {
        // Storage refused (private mode): the choice still holds for this page.
      }
      window.dispatchEvent(new CustomEvent(SORT_EVENT, { detail: { boardId, sort: next } }));
    },
    [boardId],
  );
  return [sort, choose];
}
